import React, { PureComponent } from 'react'
import {
    MessageBarType, TextField, Text,
} from '@fluentui/react'
import history from 'helpers/history'
import Invoice, { ErrorInvoice } from 'requests/objects/invoice'
import Status from 'types/status'
import InvalidEntityError from 'requests/errors/invalidEntityError'
import Card from 'components/containers/card'
import Loader from 'components/visuals/loader'
import CancelRequestError from 'requests/errors/cancelRequestError'
import NotImplementedError from 'requests/errors/notImplementedError'
import UnauthorizedError from 'requests/errors/unauthorizedError'
import { Columns } from 'react-bulma-components'
import { NEW_PATH } from 'types/others'
import { tKey, tObj } from 'helpers/methods/translate'
import PreviewFileInput from 'components/pages/home-articles/[id]/previewFileInput'
import FileInput from 'components/inputs/fileInput'
import EFileFolder from 'types/files/enums/fileFolder'
import parseJson from 'helpers/methods/parseJson'
import { getUpdatedList, isValidDate } from 'helpers/methods/common'
import FilteredVirtualCombobox from 'components/inputs/filteredVirtualCombobox'
import DatePicker from 'components/inputs/datePicker'

const DateFilterMinDate = new Date(1970, 0, 1)
const DateFilterMaxDate = new Date(2100, 11, 31)

/** @debug {import('app').AppProps} */

/**
 * @augments {PureComponent<import('app').AppProps>}
 */
export default class IdInvoices extends PureComponent {
    constructor(props) {
        super(props)
        const { match } = this.props
        this.state = {
            /** @type {Status} Current status of the component */
            status: Status.IDLE,
            /** @type {boolean} Is in readonly */
            isReadOnly: !!match?.params?.invoiceId,
            /** @type {Invoice} Element find from API */
            item: new Invoice(),
            /** @type {Invoice} Initial element fond from API. Used to refresh */
            iniItem: new Invoice(),
            /** @type {ErrorInvoice} Errors */
            errorField: new ErrorInvoice(),
            /** @type {globalThis.File} tempFile */
            tempFile: null,
        }

        this.submitInput = React.createRef()
    }

    /**
     * @inheritdoc
     */
    componentDidMount() {
        const { setMessageBar } = this.props
        setMessageBar({ isDisplayed: false })

        this.init()
    }

    /**
     * @inheritdoc
     * @param {object} prevProps Previous Props
     */
    componentDidUpdate(prevProps) {
        const {
            match, setCommand, lang, command,
        } = this.props

        if (match?.params?.invoiceId !== prevProps?.match?.params?.invoiceId)
            if (!prevProps?.match.path.includes(`/${NEW_PATH}`)) {
                this.init()
            } else {
                setCommand([])
                this.setupBreadcrumb()
                this.setupCommandBar()
                setCommand(this.commandRead)
            }

        if (lang !== prevProps.lang) {
            setCommand([])
            this.setupBreadcrumb()
            this.setupCommandBar()
            setCommand(command.find(x => x.key === 'edit') ? this.commandRead : this.commandEdit)
        }
    }

    /**
     * @inheritdoc
     */
    componentWillUnmount() {
        this.invoicesHandlerRemoveById?.cancel()
        this.invoicesHandlerGetById?.cancel()
        this.invoicesHandlerUpsert?.cancel()
    }

    /**
     * Setup breadcrumb elements
     */
    setupBreadcrumb() {
        const { setBreadcrumb, match } = this.props

        setBreadcrumb([
            { text: this.tKey('invoices'), key: 'invoice', href: '/invoices' },
            {
                text: match?.params?.invoiceId ? `${this.tKey('invoice')} n°${match?.params?.invoiceId}` : this.tKey('newInvoice'),
                key: 'one-invoice',
                isCurrentItem: true,
            },
        ])
    }

    /**
     * Setup commandbar elements
     */
    setupCommandBar() {
        const {
            setCommand, match, setModal, invoicesHandler, setMessageBar,
        } = this.props

        /**
         * @type {import('@fluentui/react').ICommandBarItemProps[]} Commanbar items when readonly
         */
        this.commandRead = [
            {
                key: 'edit',
                text: this.tKey('edit'),
                iconProps: { iconName: 'Edit' },
                onClick: () => this.setState({ isReadOnly: false }, () => setCommand(this.commandEdit)),
                disabled: false,
            },
        ]

        /**
         * @type {import('@fluentui/react').ICommandBarItemProps[]} Commanbar items when editing
         */
        this.commandEdit = [
            {
                key: 'cancel',
                text: this.tKey('cancel'),
                iconProps: { iconName: 'Cancel' },
                onClick: () => {
                    const { iniItem } = this.state
                    this.setState(
                        {
                            isReadOnly: true,
                            item: iniItem,
                            status: Status.PENDING,
                            errorField: new ErrorInvoice(),
                        },
                        () => this.setState({ status: Status.IDLE }, // Workaround to reset input with "defaultValue"
                            () => setCommand(this.commandRead)),
                    )
                },
                disabled: !match?.params?.invoiceId,
            },
            {
                key: 'save',
                text: this.tKey('save'),
                iconProps: { iconName: 'Save' },
                onClick: () => {
                    this.submitInput.current.click()
                },
            },
            {
                key: 'delete',
                text: this.tKey('delete'),
                iconProps: { iconName: 'Delete' },
                onClick: () => {
                    setModal({
                        show: true,
                        title: this.tKey('deleteInvoice'),
                        subTitle: this.tKey('sureDeleteInvoice'),
                        callback: () => {
                            this.setState({ status: Status.PENDING, isReadOnly: true }, async () => {
                                setCommand([])
                                setMessageBar({ isDisplayed: false })
                                try {
                                    this.invoicesHandlerRemoveById = invoicesHandler.removeById(match?.params?.invoiceId)
                                    await this.invoicesHandlerRemoveById.fetch()
                                    history.push('/invoices')
                                    setMessageBar({ // We must set the messagebar after change page, to force re add it
                                        isDisplayed: true,
                                        type: MessageBarType.success,
                                        message: this.tKey('modalDeleteMessage'),
                                    })
                                } catch (error) {
                                    switch (error?.constructor) {
                                        case CancelRequestError:
                                        case UnauthorizedError:
                                        case InvalidEntityError: break
                                        case NotImplementedError:
                                            // eslint-disable-next-line no-console
                                            console.error(error)
                                            break
                                        default:
                                            setCommand(this.commandEdit)
                                            this.setState({ isReadOnly: false, status: Status.REJECTED })
                                            // eslint-disable-next-line no-console
                                            console.error(error)
                                            break
                                    }
                                }
                            })
                        },
                    })
                },
                disabled: !match?.params?.invoiceId,
            },
        ]
    }

    /**
     * Init Page
     */
    init() {
        const {
            setCommand, match, location, invoicesHandler, setMessageBar,
        } = this.props

        setCommand([])
        setMessageBar({ isDisplayed: false })
        this.setupBreadcrumb()
        this.setupCommandBar()

        // If there is an id in URL, get element by id
        if (match?.params?.invoiceId)
            this.setState({ status: Status.PENDING }, async () => {
                if (location.state?.invoice) // If object came from history push with a create
                    this.setState({
                        item: location.state?.invoice,
                        iniItem: location.state?.invoice,
                        status: Status.RESOLVED,
                    }, () => {
                        setCommand([])
                        setCommand(this.commandRead)
                    })
                else
                    try {
                        this.invoicesHandlerGetById = invoicesHandler.getById(match?.params?.invoiceId)
                        const invoice = await this.invoicesHandlerGetById.fetch()
                        this.setState({
                            item: invoice,
                            iniItem: invoice,
                            status: Status.RESOLVED,
                        }, () => {
                            setCommand([])
                            setCommand(this.commandRead)
                        })
                    } catch (error) {
                        switch (error?.constructor) {
                            case CancelRequestError:
                            case UnauthorizedError:
                            case InvalidEntityError: break
                            case NotImplementedError:
                                // eslint-disable-next-line no-console
                                console.error(error)
                                break
                            default:
                                setCommand(this.commandRead)
                                this.setState({ status: Status.REJECTED })
                                // eslint-disable-next-line no-console
                                console.error(error)
                                break
                        }
                    }
            })
        else
            this.setState({ status: Status.PENDING }, () => {
                this.setState({
                    item: new Invoice(),
                    iniItem: new Invoice(),
                    status: Status.IDLE,
                }, () => {
                    this.setupCommandBar()
                    setCommand(this.commandEdit)
                })
            })
    }

    /**
     * Function called when valide form in submit, in order to save or create new entity
     */
    upsert() {
        const {
            setCommand, invoicesHandler, filesHandler, match, setMessageBar,
        } = this.props
        const { item, tempFile } = this.state

        this.setState({ status: Status.PENDING, isReadOnly: true }, async () => {
            setCommand([])
            setMessageBar({ isDisplayed: false })
            try {
                this.invoicesHandlerUpsert = invoicesHandler.upsert(item, match?.params?.invoiceId)
                const invoice = await this.invoicesHandlerUpsert.fetch()

                if (tempFile) {
                    setMessageBar({ isDisplayed: false })
                    this.handlerUploadFile = filesHandler.uploadFile(
                        invoice?.invoiceId,
                        EFileFolder.Invoice,
                        tempFile,
                    )
                    invoice.file = await this.handlerUploadFile.fetch()
                    setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementHasBeenAdded' })
                }

                this.setState({
                    item: invoice,
                    iniItem: invoice,
                    status: Status.RESOLVED,
                    errorField: new ErrorInvoice(),
                })

                this.setupCommandBar()
                setCommand(this.commandRead)

                if (!match?.params?.invoiceId)
                    history.replace(`/invoices/${invoice.invoiceId}`)
            } catch (error) {
                this.setState({ status: Status.REJECTED, isReadOnly: false }, () => {
                    switch (error?.constructor) {
                        case CancelRequestError:
                        case UnauthorizedError: break
                        case NotImplementedError:
                            // eslint-disable-next-line no-console
                            console.error(error)
                            break
                        case InvalidEntityError:
                            this.setState({ errorField: /** @type {InvalidEntityError<ErrorInvoice>} */(error).errorField })
                            setCommand(this.commandEdit)
                            break
                        default:
                            setCommand(this.commandEdit)
                            // eslint-disable-next-line no-console
                            console.error(error)
                            break
                    }
                })
            }
        })
    }

    /**
     * Render component
     * @returns {JSX.Element} Element
     */
    render() {
        const {
            item, status, isReadOnly, errorField, tempFile,
        } = this.state
        const { filesHandler, param: { clients }, lang } = this.props

        if (status === Status.PENDING)
            return <Loader />

        return (
            <main>
                <form
                    onSubmit={ev => {
                        ev.preventDefault()
                        this.upsert()
                    }}
                >
                    <Card
                        title={this.tKey('information')}
                        iconName="PageData"
                    >
                        <Columns>
                            <Columns.Column size="one-quarter">
                                <FilteredVirtualCombobox
                                    label={this.tKey('client')}
                                    readOnly={isReadOnly}
                                    options={clients}
                                    selectedKey={item.clientId}
                                    onChange={(_ev, option) => this.setState({ item: { ...item, clientId: option.key } })}
                                    errorMessage={this.tObj(parseJson(errorField.clientId))}
                                    required={!isReadOnly}
                                />
                            </Columns.Column>
                            <Columns.Column size="one-quarter">
                                <TextField
                                    label={this.tKey('invoiceNumber')}
                                    placeholder={this.tKey('invoiceNumber')}
                                    value={item.invoiceNumber}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    onChange={(ev, newVal) => this.setState({
                                        item: {
                                            ...item, invoiceNumber: newVal,
                                        },
                                    })}
                                    errorMessage={this.tObj(parseJson(errorField.invoiceNumber))}
                                    required={!isReadOnly}
                                />
                            </Columns.Column>
                            <Columns.Column size="one-quarter">
                                <DatePicker
                                    label={this.tKey('invoiceDate')}
                                    placeholder={this.tKey('invoiceDate')}
                                    date={item.date}
                                    minDate={DateFilterMinDate}
                                    maxDate={DateFilterMaxDate}
                                    lang={lang}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    onChangeDate={date => this.setState({
                                        item: {
                                            ...item, date: isValidDate(new Date(date)) ? /** @type {any} */(new Date(date)) : '',
                                        },
                                    })}
                                    errorMessage={this.tObj(parseJson(errorField.date))}
                                    required={!isReadOnly}
                                />
                            </Columns.Column>
                            <Columns.Column size="one-quarter">
                                <DatePicker
                                    label={this.tKey('deadlineDate')}
                                    placeholder={this.tKey('deadlineDate')}
                                    date={item.deadlineDate}
                                    minDate={DateFilterMinDate}
                                    maxDate={DateFilterMaxDate}
                                    lang={lang}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    onChangeDate={date => this.setState({
                                        item: {
                                            ...item, deadlineDate: isValidDate(new Date(date)) ? /** @type {any} */(new Date(date)) : '',
                                        },
                                    })}
                                    errorMessage={this.tObj(parseJson(errorField.deadlineDate))}
                                    required={!isReadOnly}
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns>
                            <Columns.Column size="one-quarter">
                                <TextField
                                    label={this.tKey('amountExcludingVat')}
                                    placeholder={this.tKey('amountExcludingVat')}
                                    value={item.amountExcludingTax?.toString() || ''}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    onChange={(ev, newVal) => this.setState({ item: { ...item, amountExcludingTax: +newVal } })}
                                    errorMessage={this.tObj(parseJson(errorField.amountExcludingTax))}
                                    required={!isReadOnly}
                                    type="number"
                                    // eslint-disable-next-line react/no-unstable-nested-components
                                    onRenderInput={(props, defaultRender) => (isReadOnly
                                        ? (
                                            <Text style={{ paddingTop: '6px' }}>
                                                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(item.amountExcludingTax)}
                                            </Text>
                                        )
                                        : defaultRender(props)
                                    )}
                                />
                            </Columns.Column>
                            <Columns.Column size="one-quarter">
                                <TextField
                                    label={this.tKey('amountIncludingVat')}
                                    placeholder={this.tKey('amountIncludingVat')}
                                    value={item.amountIncludingTax?.toString() || ''}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    onChange={(ev, newVal) => this.setState({ item: { ...item, amountIncludingTax: +newVal } })}
                                    errorMessage={this.tObj(parseJson(errorField.amountIncludingTax))}
                                    required={!isReadOnly}
                                    type="number"
                                    // eslint-disable-next-line react/no-unstable-nested-components
                                    onRenderInput={(props, defaultRender) => (isReadOnly
                                        ? (
                                            <Text style={{ paddingTop: '6px' }}>
                                                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(item.amountIncludingTax)}
                                            </Text>
                                        )
                                        : defaultRender(props)
                                    )}
                                />
                            </Columns.Column>
                            <Columns.Column size="one-quarter">
                                <TextField
                                    label={this.tKey('url')}
                                    placeholder={this.tKey('url')}
                                    value={item.url ? item.url : ''}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    onChange={(ev, newVal) => this.setState({ item: { ...item, url: newVal } })}
                                    errorMessage={this.tObj(this.parseJson(errorField.url))}
                                    required={!isReadOnly && !item.file?.fileId}
                                    disabled={!!item.file?.fileId || !!tempFile}
                                    type="url"
                                />
                            </Columns.Column>
                            <Columns.Column size="one-quarter">
                                {item.invoiceId
                                    ? (
                                        <PreviewFileInput
                                            label={this.tKey('file')}
                                            handler={filesHandler}
                                            fileFolderId={EFileFolder.Invoice}
                                            entityId={item.invoiceId}
                                            isReadOnly={isReadOnly}
                                            file={item.file}
                                            updateItem={newFile => this.setState({ item: { ...item, file: newFile } })}
                                            isDisabled={!!item.url}
                                            isRequired={!isReadOnly && !item.url}
                                        />
                                    )
                                    : (
                                        <FileInput
                                            label={this.tKey('file')}
                                            isReadOnly={false}
                                            isDisabled={status === Status.PENDING || !!item.url}
                                            fileName={tempFile?.name}
                                            tooltipContent=""
                                            onDownload={() => null}
                                            onUpload={file => Promise.resolve(this.setState({ tempFile: file }))}
                                            onDelete={() => Promise.resolve(this.setState({ tempFile: null }))}
                                            isRequired={!isReadOnly && !item.url}
                                        />
                                    )}
                            </Columns.Column>
                        </Columns>
                    </Card>
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <button
                        type="submit"
                        style={{ display: 'none' }}
                        ref={this.submitInput}
                        tabIndex={-1}
                    />
                </form>
            </main>
        )
    }
}

IdInvoices.prototype.getUpdatedList = getUpdatedList
IdInvoices.prototype.tKey = tKey
IdInvoices.prototype.tObj = tObj
IdInvoices.prototype.parseJson = parseJson
